import * as genericService from "./genericFormServices";
import type { LegalAdviceFormSevenData } from "../types/LegalAdvices/LegalAdviceFormSeven.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-seven";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormSevenData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Seven
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormSevenData => {
  if (!item) return {} as LegalAdviceFormSevenData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormSeven ||
    item.legalAdviceForm ||
    item.formData ||
    {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_SEVEN',
    status: item.status || form.status || 'DRAFT',

    // Form Seven Specific Text Fields
    office: form.office || "",
    
    // Page 1: Items 1 to 14
    registerNo: form.registerNo || "",
    adviceCaseNo: form.adviceCaseNo || "",
    originalCourtAndCaseNo: form.originalCourtAndCaseNo || "",
    prosecutedLawSection: form.prosecutedLawSection || "",
    chargedLawSection: form.chargedLawSection || "",
    appealCourtAndCaseNo: form.appealCourtAndCaseNo || "",
    acquittalDate: form.acquittalDate || "",
    applicant: form.applicant || "",
    applicationDate: form.applicationDate || "",
    judgmentCopyApplyDate: form.judgmentCopyApplyDate || "",
    judgmentCopyReceiveDate: form.judgmentCopyReceiveDate || "",
    reviewSubmitDeadline: form.reviewSubmitDeadline || "",
    reviewSubmitDate: form.reviewSubmitDate || "",
    respondentDetails: form.respondentDetails || "",

    // Page 2: Summaries and Statements
    caseSummary: form.caseSummary || "",
    prosecutionWitnessStatements: form.prosecutionWitnessStatements || "",
    defenseWitnessStatements: form.defenseWitnessStatements || "",
    courtWitnessStatements: form.courtWitnessStatements || "",
    advocateOpinionProsecution: form.advocateOpinionProsecution || "",
    advocateOpinionDefense: form.advocateOpinionDefense || "",

    // Page 3: Specific Reviews and Final Signatures
    exhibitsReview: form.exhibitsReview || "",
    courtJudgmentReviewOriginal: form.courtJudgmentReviewOriginal || "",
    courtJudgmentReviewAppeal: form.courtJudgmentReviewAppeal || "",
    appealFitnessOpinion: form.appealFitnessOpinion || "",

    // Advocate Signatures
    advocateSignDate: form.advocateSignDate || "",
    advocateSignName: form.advocateSignName || "",
    advocateSignatureImage: formatSignatureUrl(form.advocateSignatureImage),

    // Officer Reviews & Signatures
    officerFitnessOpinion: form.officerFitnessOpinion || "",
    officerReceiveDate: form.officerReceiveDate || "",
    officerSubmitDate: form.officerSubmitDate || "",
    officerSignName: form.officerSignName || "",
    officerSignatureImage: formatSignatureUrl(form.officerSignatureImage),

    regionFitnessOpinion: form.regionFitnessOpinion || "",
    regionReceiveDate: form.regionReceiveDate || "",
    regionSubmitDate: form.regionSubmitDate || "",
    regionSignName: form.regionSignName || "",
    regionSignatureImage: formatSignatureUrl(form.regionSignatureImage),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Seven
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormSevenData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Form Seven Date Matching
  const formattedAppDate = app.applicationDate || app.submitDate || '';

  const matchesDate = !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  // Search matching across Form Seven fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.registerNo && app.registerNo.toLowerCase().includes(term)) ||
    Boolean(app.adviceCaseNo && app.adviceCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.originalCourtAndCaseNo && app.originalCourtAndCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.prosecutedLawSection && app.prosecutedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.chargedLawSection && app.chargedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.applicant && app.applicant.toLowerCase().includes(term)) ||
    Boolean(app.advocateSignName && app.advocateSignName.toLowerCase().includes(term)) ||
    Boolean(app.officerSignName && app.officerSignName.toLowerCase().includes(term)) || 
    Boolean(app.regionSignName && app.regionSignName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormSevenData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormSevenData> =>
  genericService.fetchApplicationById<LegalAdviceFormSevenData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormSevenData>
) => genericService.saveDraft<LegalAdviceFormSevenData>(ENDPOINT, id, data);

export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormSevenData
) => genericService.submitForm<LegalAdviceFormSevenData>(ENDPOINT, id, data);

export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);